/**
 * Cache Management Utilities
 * 
 * This module provides utilities for managing caches and preventing stale data
 * issues when navigating between different patients and encounters.
 */

// Global cache registry
const cacheRegistry = new Map();

/**
 * Register a cache with the registry
 * @param {string} cacheName - Name of the cache
 * @param {Object} cache - Cache object with clear method
 */
export const registerCache = (cacheName, cache) => {
  if (cache && typeof cache.clear === 'function') {
    cacheRegistry.set(cacheName, cache);
    console.log(`Registered cache: ${cacheName}`);
  }
};

/**
 * Unregister a cache from the registry
 * @param {string} cacheName - Name of the cache
 */
export const unregisterCache = (cacheName) => {
  cacheRegistry.delete(cacheName);
  console.log(`Unregistered cache: ${cacheName}`);
};

/**
 * Clear all registered caches
 */
export const clearAllCaches = () => {
  console.log('Clearing all registered caches');
  cacheRegistry.forEach((cache, name) => {
    try {
      cache.clear();
      console.log(`Cleared cache: ${name}`);
    } catch (error) {
      console.error(`Error clearing cache ${name}:`, error);
    }
  });
};

/**
 * Clear caches for a specific patient
 * @param {string} patientId - Patient ID to clear caches for
 */
export const clearPatientCaches = (patientId) => {
  if (!patientId) return;
  
  console.log(`Clearing caches for patient: ${patientId}`);
  
  // Clear registered caches
  cacheRegistry.forEach((cache, name) => {
    if (cache.clearForPatient && typeof cache.clearForPatient === 'function') {
      try {
        cache.clearForPatient(patientId);
        console.log(`Cleared patient-specific cache: ${name}`);
      } catch (error) {
        console.error(`Error clearing patient cache ${name}:`, error);
      }
    }
  });
  
  // Clear browser caches
  if ('caches' in window) {
    caches.keys().then(cacheNames => {
      const patientCaches = cacheNames.filter(name => 
        name.includes('patient') || name.includes('encounter') || name.includes('procedure')
      );
      
      return Promise.all(patientCaches.map(name => caches.delete(name)));
    }).then(() => {
      console.log('Cleared browser caches');
    }).catch(error => {
      console.error('Error clearing browser caches:', error);
    });
  }
  
  // Dispatch custom event for components to clear their own caches
  window.dispatchEvent(new CustomEvent('clearPatientCaches', {
    detail: { patientId }
  }));
};

/**
 * Clear caches for a specific encounter
 * @param {string} encounterId - Encounter ID to clear caches for
 * @param {string} patientId - Patient ID (optional, for additional context)
 */
export const clearEncounterCaches = (encounterId, patientId = null) => {
  if (!encounterId) return;
  
  console.log(`Clearing caches for encounter: ${encounterId}${patientId ? ` (patient: ${patientId})` : ''}`);
  
  // Clear registered caches
  cacheRegistry.forEach((cache, name) => {
    if (cache.clearForEncounter && typeof cache.clearForEncounter === 'function') {
      try {
        cache.clearForEncounter(encounterId);
        console.log(`Cleared encounter-specific cache: ${name}`);
      } catch (error) {
        console.error(`Error clearing encounter cache ${name}:`, error);
      }
    }
  });
  
  // Clear browser caches
  if ('caches' in window) {
    caches.keys().then(cacheNames => {
      const encounterCaches = cacheNames.filter(name => 
        name.includes('encounter') || name.includes('procedure')
      );
      
      return Promise.all(encounterCaches.map(name => caches.delete(name)));
    }).then(() => {
      console.log('Cleared browser caches');
    }).catch(error => {
      console.error('Error clearing browser caches:', error);
    });
  }
  
  // Dispatch custom event for components to clear their own caches
  window.dispatchEvent(new CustomEvent('clearEncounterCaches', {
    detail: { encounterId, patientId }
  }));
};

/**
 * Get cache statistics
 * @returns {Object} Cache statistics
 */
export const getCacheStats = () => {
  const stats = {
    totalCaches: cacheRegistry.size,
    cacheNames: Array.from(cacheRegistry.keys()),
    timestamp: Date.now()
  };
  
  return stats;
};

/**
 * Hook for components to listen to cache clearing events
 * @param {Function} callback - Callback function to execute when caches are cleared
 * @param {Array} dependencies - Dependencies for the useEffect
 */
export const useCacheClearing = (callback, dependencies = []) => {
  // This hook should be used in React components
  // The actual implementation would be in a separate React hook file
  console.warn('useCacheClearing should be used in React components, not in utility files');
};

const cacheUtils = {
  registerCache,
  unregisterCache,
  clearAllCaches,
  clearPatientCaches,
  clearEncounterCaches,
  getCacheStats,
  useCacheClearing
};

export default cacheUtils;
